import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type MeResponse } from '../api/client'
import { resolveTheme } from '../lib/theme'
import { ThemeSwitch } from '../editorial/ThemeSwitch'
import { deskFooterLink, deskNav } from './nav'
import { deskHref } from './links'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'

/** The Subscriber Desk shell: its own full-screen area, outside the console's NavRail.
 *  Shares the Editorial Desk screen style (the same editorial_theme preference). */
export function DeskLayout() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq?.addEventListener) return
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const pref = me?.preferences?.editorial_theme ?? 'light'
  const theme = resolveTheme(pref, systemDark)
  useEffect(() => {
    if (!me) return
    document.documentElement.dataset.theme = theme
    return () => { delete document.documentElement.dataset.theme }
  }, [me, theme])
  if (!me) return null
  const back = deskFooterLink(me.role)

  async function signOut() {
    try { await api.post('/api/auth/logout', {}) } catch { /* the session is gone either way */ }
    qc.clear()
    navigate('/login', { replace: true })
  }

  return (
    <div data-theme={theme} className="min-h-screen bg-ink-900 text-paper font-ui text-[14px] md:flex">
      <nav aria-label="Subscriber Desk" className="flex w-full shrink-0 flex-col gap-6 border-b border-ink-500 bg-ink-700 px-3.5 pb-7 pt-6 md:min-h-screen md:w-[264px] md:border-b-0 md:border-r">
        <div className="flex flex-col gap-0.5 px-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-glow">AAMCirc</span>
          <span className="font-serif text-[23px] font-medium tracking-tight">Subscriber Desk</span>
        </div>

        <div className="flex flex-col gap-1">
          {deskNav(me.role).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === deskHref()}
              className={({ isActive }) => `rounded-lg px-2.5 py-[9px] font-semibold text-paper ${focus} ${isActive ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>

        <div className="mt-auto flex flex-col gap-3 border-t border-ink-500 px-2.5 pt-3.5 text-[13px]">
          <span className="text-paper-faint">Signed in as <strong className="break-all font-semibold text-paper">{me.email}</strong></span>
          <ThemeSwitch value={pref} />
          <NavLink to={deskHref('help')} className={({ isActive }) => `underline underline-offset-2 hover:text-paper ${focus} ${isActive ? 'text-paper' : 'text-amber-glow'}`}>User manual</NavLink>
          {back ? (
            <NavLink to={back.to} className={`text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>{back.label}</NavLink>
          ) : (
            <button type="button" onClick={signOut} className={`self-start text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>Sign out</button>
          )}
        </div>
      </nav>
      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  )
}
